// src/components/dashboard/OrderDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

/* ---- Helpers ---- */
const normalizeCap = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
const parseNum = (v) => {
  const n = Number.parseFloat(String(v ?? 0).toString().replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

/* toArray: يحوّل أي قيمة لمصفوفة أسماء موديلات قابلة للعرض */
const toArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((x) =>
        typeof x === "string"
          ? x.trim()
          : (x?.model_name || x?.name || x?.label || String(x || "")).trim()
      )
      .filter(Boolean);
  }
  if (typeof val === "string") {
    const s = val.trim();
    // جرّب JSON أولاً
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return toArray(parsed);
    } catch {}
    // ثم CSV أو فواصل شائعة
    return s.split(/[,|/]+/).map((t) => t.trim()).filter(Boolean);
  }
  if (typeof val === "object") {
    const s = val?.model_name || val?.name || val?.label;
    return s ? [String(s).trim()] : [];
  }
  return [];
};

const getModels = (item) => {
  const d = item?.item_data || {};
  const candidates = [
    item?.model_name,               // من عمود order_items.model_name
    d?.model_name,                  // إن وُجد داخل item_data
    item?.selected_models,
    d?.selected_models,
    item?.accessories_selected_models,
    d?.accessories_selected_models,
    item?.models,
    d?.models,
    item?.selected_model,
    d?.selected_model,
  ];
  for (const c of candidates) {
    const arr = toArray(c);
    if (arr.length) return arr;
  }
  return [];
};

const resolveUnitPrice = (item) => {
  // 1) مباشرة من الطلب
  const direct =
    item?.unit_price ??
    item?.price ??
    item?.item_data?.product_price ??
    null;
  if (direct != null && parseNum(direct) > 0) return parseNum(direct);

  // 2) من خريطة أسعار السعات (يدعم كائن أو JSON-string)
  const cap = normalizeCap(item?.variant_capacity ?? item?.item_data?.selected_capacity);
  const mapsRaw = [
    item?.item_data?.product_capacity_prices,
    item?.product?.product_capacity_prices,
    item?.product_capacity_prices,
  ].filter(Boolean);

  for (let m of mapsRaw) {
    if (typeof m === "string") {
      try { m = JSON.parse(m); } catch { m = null; }
    }
    if (m && typeof m === "object") {
      for (const [k, v] of Object.entries(m)) {
        if (normalizeCap(k) === cap) return parseNum(v);
      }
    }
  }

  // 3) fallback السعر الأساسي
  const base = item?.product?.product_price ?? 0;
  return parseNum(base);
};

export default function OrderDetails() {
  const { id } = useParams();
  const API_BASE = "https://laravel-api.aj-mart.com/api";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // attach token
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API_BASE}/orders/${id}`);
        if (res.data?.status) setOrder(res.data.data?.order || null);
        else setOrder(null);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const toMoney = (n) => parseNum(n).toFixed(2);

  const getItemName = (item) => {
    if (!item) return "Unknown";
    if (item.product_id != null && item.product) return item.product.product_name_E;
    if (item.accessories_id != null && item.accessory) return item.accessory.accessories_name_E;
    if (item.item_data?.product_name_E) return item.item_data.product_name_E;
    return "Unknown";
  };

  const getItemImage = (item) => {
    const base = "https://laravel-api.aj-mart.com/storage/app/public/";
    if (item?.product?.product_main_img) return base + item.product.product_main_img;
    if (item?.accessory?.accessories_main_img) return base + item.accessory.accessories_main_img;
    if (item?.item_data?.product_main_img) return base + item.item_data.product_main_img;
    return null;
  };

  const getItemColor = (item) => item?.variant_color || item?.item_data?.selected_color || null;
  const getItemCapacity = (item) => item?.variant_capacity || item?.item_data?.selected_capacity || null;

  const couponDisplay = (o) =>
    o?.coupon_code ||
    o?.coupon?.code ||
    o?.couponId ||
    o?.coupon_id ||
    o?.meta?.coupon_code ||
    o?.order_meta?.coupon_code ||
    "—";

  const discountAmount = (o) =>
    parseNum(o?.discount ?? o?.discount_amount ?? o?.discount_total ?? 0);

  if (loading) return <div className="py-[25px]">Loading order details...</div>;
  if (!order) return <div className="py-[25px]">Order not found</div>;

  return (
    <section className="py-[25px]">
      <div className="main-title mb-[20px]">Order Details</div>

      {/* Summary Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="font-bold text-[#1485a6] mb-1">Customer</div>
          <div className="space-y-1">
            <div>
              <span className="font-semibold">Name:</span> {order.full_name || "—"}
            </div>
            <div>
              <span className="font-semibold">Phone:</span> {order.phone || "—"}
            </div>
            <div>
              <span className="font-semibold">Email:</span> {order.email || "—"}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="font-bold text-[#1485a6] mb-1">Shipping</div>
          <div className="space-y-1">
            <div>
              <span className="font-semibold">Address:</span> {order.address || "—"}
            </div>
            <div>
              <span className="font-semibold">City:</span> {order.city || "—"}
            </div>
            <div>
              <span className="font-semibold">Apartment:</span> {order.apartment_details || "—"}
            </div>
            <div>
              <span className="font-semibold">Notes:</span> {order.order_notes || "—"}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="font-bold text-[#1485a6] mb-1">Order</div>
          <div className="space-y-1">
            <div><span className="font-semibold">ID:</span> #{order.id}</div>
            <div><span className="font-semibold">Payment Type:</span> {order.payment_type || "—"}</div>
            <div><span className="font-semibold">Payment Status:</span> {order.payment_status || "Pending"}</div>
            <div><span className="font-semibold">Order Status:</span> {order.order_status || "Pending"}</div>
            <div><span className="font-semibold">Created:</span> {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}</div>
            <div><span className="font-semibold">Updated:</span> {order.updated_at ? new Date(order.updated_at).toLocaleString() : "—"}</div>
            <div><span className="font-semibold">Coupon:</span> {couponDisplay(order)}</div>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-semibold">{toMoney(order.subtotal ?? order.items_subtotal)} JOD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Delivery:</span>
            <span className="font-semibold">1.00 JOD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Discount:</span>
            <span className="font-semibold">-{toMoney(discountAmount(order))} JOD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total:</span>
            <span className="font-semibold">{ parseNum(toMoney(order.total ?? order.grand_total)) + 1 } JOD</span>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200 text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="border border-gray-200 p-[10px]">Image</th>
              <th className="border border-gray-200 p-[10px]">Item</th>
              <th className="border border-gray-200 p-[10px]">Color</th>
              <th className="border border-gray-200 p-[10px]">Capacity</th>
              <th className="border border-gray-200 p-[10px]">Model(s)</th>{/* NEW */}
              <th className="border border-gray-200 p-[10px]">Qty</th>
              <th className="border border-gray-200 p-[10px]">Unit Price</th>
              <th className="border border-gray-200 p-[10px]">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item, idx) => {
              const name = getItemName(item);
              const img = getItemImage(item);
              const color = getItemColor(item);
              const capacity = getItemCapacity(item);
              const models = getModels(item); // NEW
              const qty = parseNum(item.quantity) || 0;
              const price = resolveUnitPrice(item);
              const lineTotal = (qty * price).toFixed(2);
              return (
                <tr key={item.id ?? idx} className="bg-white hover:bg-gray-50">
                  <td className="border border-gray-200 p-[10px]">
                    {img ? (
                      <img src={img} alt={name} className="w-14 h-14 object-contain rounded border" />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="border border-gray-200 p-[10px]">{name}</td>
                  <td className="border border-gray-200 p-[10px]">{color || "—"}</td>
                  <td className="border border-gray-200 p-[10px]">{capacity || "—"}</td>
                  <td className="border border-gray-200 p-[10px]">
                    {models.length ? models.join(", ") : "—"}
                  </td>
                  <td className="border border-gray-200 p-[10px]">{qty}</td>
                  <td className="border border-gray-200 p-[10px]">{price.toFixed(2)} JOD</td>
                  <td className="border border-gray-200 p-[10px]">{lineTotal} JOD</td>
                </tr>
              );
            })}
            {!order.items?.length && (
              <tr>
                <td className="border p-[10px]" colSpan={8}>
                  No items
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <Link to="/orders" className="btn bg-gray-200 hover:bg-gray-300">
          Back to Orders
        </Link>
      </div>
    </section>
  );
}
